<template>
  <v-container class="px-0 pb-5 health-tracker" style="background-color:#F5F5F5;min-height:500px;">
    <!-- Active Tracker Section -->
    <v-sheet v-if="currentTracker && hasActiveTracker" class="mx-6 border-rounded">
      <v-container class="d-flex justify-space-between align-center pa-2 pb-0 w-100">
        <v-card-title class="text-h6 font-weight" style="width: 90%">
          <span v-if="trackerHasStarted">My Progress</span>
          <span v-else>My Tracker</span>
          <span v-if="currentItem" class="primary-color overview-name">
            ({{ currentItem?.name }})
          </span>
        </v-card-title>

        <div class="pr-2 d-flex" style="width: 10%">
          <LeftAndRightArrows
              @left="previousItem"
              @right="nextItem"
          />
        </div>
      </v-container>
      
      <v-container>
        <!-- Tracker Not Started Message -->
        <div v-if="!trackerHasStarted" class="text-center pa-4">
          <v-icon size="48" color="primary" class="mb-3">fa fa-calendar-clock</v-icon>
          <p class="text-h6 mb-2">Tracker starts on {{ moment(currentTracker?.start_date).format('DD MMM YYYY') }}</p>
          <p class="text-body-2 text-grey-darken-1">
            Your tracker will become active and you'll be able to start logging entries from this date.
          </p>
        </div>

        <!-- Progress Chart Area (only show if tracker has started) -->
        <div v-else class="d-flex justify-center">
          <div class="chart-container">
            <svg
              :width="chartWidth"
              :height="chartHeight"
              class="line-chart"
              v-if="periodBasedEntries.length > 0"
              :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
            >
              <!-- Grid lines -->
              <g class="grid">
                <!-- Horizontal grid lines -->
                <line
                  v-for="tick in yAxisTicks"
                  :key="`h-grid-${tick}`"
                  :x1="chartPadding"
                  :y1="getYPosition(tick)"
                  :x2="chartWidth - chartPadding"
                  :y2="getYPosition(tick)"
                  stroke="#E0E0E0"
                  stroke-width="1"
                />
                <!-- Vertical grid lines -->
                <line
                  v-for="(entry, index) in periodBasedEntries"
                  :key="`v-grid-${index}`"
                  :x1="getXPosition(index)"
                  :y1="chartPadding"
                  :x2="getXPosition(index)"
                  :y2="chartHeight - chartPadding - 40"
                  stroke="#E0E0E0"
                  stroke-width="1"
                />
              </g>

              <!-- Chart line -->
              <path
                :d="chartPath"
                :stroke="settingsStore.settings.primaryColor"
                stroke-width="3"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                :stroke-dasharray="hasAnySubmittedEntries ? 'none' : '5,5'"
                :opacity="hasAnySubmittedEntries ? '1' : '0.5'"
              />

              <!-- Data points -->
              <circle
                v-for="(entry, index) in periodBasedEntries"
                :key="`point-${index}`"
                :cx="getXPosition(index)"
                :cy="getYPosition(entry.value)"
                r="8"
                :fill="entry.isSubmitted ? settingsStore.settings.primaryColor : '#FFFFFF'"
                :stroke="settingsStore.settings.primaryColor"
                :stroke-width="2"
                :fill-opacity="entry.isSubmitted ? '1' : '1'"
                class="data-point"
              />

              <!-- Y-axis labels -->
              <text
                v-for="tick in yAxisTicks"
                :key="`y-label-${tick}`"
                :x="chartPadding - 10"
                :y="getYPosition(tick) + 5"
                text-anchor="end"
                class="axis-label"
              >
                {{ tick }}
              </text>

              <!-- X-axis labels -->
              <g v-if="false">
                <text
                  v-for="(entry, index) in periodBasedEntries"
                  :key="`x-label-${index}`"
                  :x="getXPosition(index)"
                  :y="chartHeight - chartPadding + 20"
                  text-anchor="middle"
                  class="axis-label"
                >
                  {{ periodLabels[index] }}
                </text>
              </g>

              <!-- Value labels on points -->
              <text
                v-for="(entry, index) in periodBasedEntries"
                :key="`value-label-${index}`"
                :x="getXPosition(index)"
                :y="getYPosition(entry.value) - 15"
                text-anchor="middle"
                :class="entry.isSubmitted ? 'value-label' : 'value-label-unsubmitted'"
              >
                {{ entry.isSubmitted ? entry.value : '-' }}
              </text>
            </svg>

            <!-- No data message -->
            <div v-else class="text-center pa-4">
              <p class="text-body-2 text-grey-darken-1">No data to display</p>
            </div>
          </div>
        </div>
      </v-container>


    </v-sheet>

    <!-- No Active Tracker Message -->
    <v-sheet v-else class="mx-6 pa-3 border-rounded d-flex align-center justify-center" style="min-height: 300px;">
      <div class="text-center">
        <v-icon size="64" color="grey-lighten-1" class="mb-4">fa fa-chart-line</v-icon>
        <p class="text-h6 text-grey-darken-1">No tracker active</p>
        <p class="text-body-2 text-grey-darken-1 mt-2">
          Contact your practitioner to set up a health tracker.
        </p>
      </div>
    </v-sheet>

    <!-- Upcoming Entries Section -->
    <v-card-title class="mx-3 text-h6 font-weight mt-3">Upcoming Entry</v-card-title>
    <v-sheet v-if="state.upcomingEntry && Object.keys(state.upcomingEntry).length > 0"
             class="mx-6 pa-3 border-rounded d-flex align-center justify-space-between">
      <div>
        <p class="p-text date-label">
          {{ moment(state.upcomingEntry?.due_date).format('DD MMM YYYY') }}
          <span class="small">{{ getDueOrOverdue }} </span>
        </p>
        <p class="p-text mt-1">{{ state.upcomingEntry?.tracker_template?.name }}</p>
      </div>

      <v-btn
          v-if="trackerHasStarted"
          :color="getDueOrOverdue.length === 0 || disableEntryIfCompletedToday ? '#ABAAAA' : settingsStore.settings.buttonColor2"
          :disabled="getDueOrOverdue.length === 0 || disableEntryIfCompletedToday"
          class="text-white b-round-20 complete-button"
          @click="onCompleteEntry"
      >
        {{ upcomingButtonLabel }}
      </v-btn>
      <v-chip
          v-else
          color="info"
          variant="outlined"
          class="b-round-20"
      >
        Available from {{ moment(currentTracker?.start_date).format('DD MMM') }}
      </v-chip>
    </v-sheet>
    <v-sheet v-else class="mx-6 pa-3 border-rounded d-flex align-center justify-space-between">
      <p class="p-text">You have no entries to complete at this time.</p>
    </v-sheet>

    <!-- Latest Entries Section -->
    <v-card-title class="mx-3 text-h6 font-weight mt-3">Latest Entries</v-card-title>

    <div v-for="entry in latestEntriesByDate" :key="`entry-${entry.id}`">
      <v-sheet class="mx-6 pa-3 border-rounded mb-2">
        <div class="d-flex align-center justify-space-between">
          <p class="p-text date-label mb-0">
            {{ moment(entry.entry_datetime).format('DD MMM YYYY') }}
          </p>
          <v-btn
            class="b-round-20 view-button"
            @click="router.push({ name: 'tracker-entry', params: { id: currentTracker?.id, date: moment(entry.entry_datetime).format('YYYY-MM-DD') }, query: { readonly: 'true' } })"
          >
            View
          </v-btn>
        </div>
      </v-sheet>
    </div>

    <v-sheet
      v-if="latestEntriesByDate.length === 0"
      class="mx-6 pa-3 border-rounded d-flex align-center justify-space-between"
    >
      <p class="p-text">
        <span v-if="!trackerHasStarted">Entries will appear here once your tracker starts on {{ moment(currentTracker?.start_date).format('DD MMM YYYY') }}</span>
        <span v-else>There is no information to show</span>
      </p>
    </v-sheet>


  </v-container>
</template>

<script lang="ts" setup>
import {useSettingsStore} from "@/api/store/settings";
import {computed, reactive, ref, watch, onMounted, onActivated} from "vue";
import {healthTrackerService, type HealthTracker, type TrackerTemplate} from "@/api/services/healthTracker";
import {useRouter} from "vue-router";
import moment from "moment/moment";
import LeftAndRightArrows from "@/views/DocumentComponents/DocumentSurveys/Partials/LeftAndRightArrows.vue";


const router = useRouter();
const settingsStore = useSettingsStore();

const buttonColor = settingsStore.settings.buttonColor;

const state = reactive({
  activeTracker: null as HealthTracker | null,
  trackerTemplates: [] as TrackerTemplate[],
  allTrackerEntries: [] as any[],
  trackerEntriesWithValue: [] as any[],
  upcomingEntry: {} as any,
  latestEntries: [] as any[],
  currentItemIndex: 0,
  availableItems: [] as any[],
});



const selectedTemplate = ref<TrackerTemplate | null>(null);

const hasActiveTracker = computed(() => {
  return state.activeTracker && state.activeTracker.is_active;
});

const trackerHasStarted = computed(() => {
  if (!state.activeTracker) return false;
  
  const today = moment();
  const startDate = moment(state.activeTracker.start_date);
  return today.isSameOrAfter(startDate, 'day');
});

const currentTracker = computed(() => {
  return state.activeTracker;
});

const currentItem = computed(() => {
  if (state.availableItems.length === 0) return null;
  return state.availableItems[state.currentItemIndex];
});

const latestEntriesByDate = computed(() => {
  // Only completed entries (isSubmitted true or value not null)
  const completed = state.latestEntries.filter((entry: any) => entry.value !== null && entry.value !== undefined);
  // Group by date (YYYY-MM-DD), pick the latest by entry_datetime per date
  const grouped: { [date: string]: any } = {};
  completed.forEach((entry: any) => {
    const date = moment(entry.entry_datetime).format('YYYY-MM-DD');
    if (!grouped[date] || moment(entry.entry_datetime).isAfter(grouped[date].entry_datetime)) {
      grouped[date] = entry;
    }
  });
  // Return sorted by date descending
  return Object.values(grouped).sort((a: any, b: any) => moment(b.entry_datetime).diff(moment(a.entry_datetime)));
});

// Chart-related computed properties and methods
const chartWidth = computed(() => {
  const containerWidth = Math.min(window.innerWidth - 80, 600); // Responsive width with max
  return Math.max(containerWidth, 300); // Minimum width
});
const chartHeight = 250;
const chartPadding = 50;

const minValue = computed(() => {
  if (selectedTemplate.value?.scale_min !== undefined) return selectedTemplate.value.scale_min;
  if (currentTracker.value?.tracker_template?.scale_min !== undefined) return currentTracker.value.tracker_template.scale_min;
  return 0;
});

const maxValue = computed(() => {
  if (selectedTemplate.value?.scale_max !== undefined) return selectedTemplate.value.scale_max;
  if (currentTracker.value?.tracker_template?.scale_max !== undefined) return currentTracker.value.tracker_template.scale_max;
  return 10;
});

const yAxisTicks = computed(() => {
  const ticks = [];
  const step = Math.max(1, Math.floor((maxValue.value - minValue.value) / 5));
  for (let i = minValue.value; i <= maxValue.value; i += step) {
    ticks.push(i);
  }
  if (ticks[ticks.length - 1] !== maxValue.value) {
    ticks.push(maxValue.value);
  }
  return ticks;
});

const getXPosition = (index: number) => {
  const availableWidth = chartWidth.value - (chartPadding * 2);
  const stepWidth = periodBasedEntries.value.length > 1 
    ? availableWidth / (periodBasedEntries.value.length - 1)
    : availableWidth / 2; // Center single point
  return chartPadding + (index * stepWidth);
};

const getYPosition = (value: number) => {
  const availableHeight = chartHeight - (chartPadding * 2) - 40; // Extra space for x-axis labels
  const valueRange = maxValue.value - minValue.value;
  if (valueRange === 0) return chartPadding + availableHeight / 2; // Center if no range
  const normalizedValue = (value - minValue.value) / valueRange;
  return chartPadding + (availableHeight * (1 - normalizedValue));
};

const chartPath = computed(() => {
  if (periodBasedEntries.value.length === 0) return '';
  
  let path = '';
  periodBasedEntries.value.forEach((entry, index) => {
    const x = getXPosition(index);
    const y = getYPosition(entry.value);
    
    if (index === 0) {
      path += `M ${x} ${y}`;
    } else {
      path += ` L ${x} ${y}`;
    }
  });
  
  return path;
});

const hasAnySubmittedEntries = computed(() => {
  return periodBasedEntries.value.some(entry => entry.isSubmitted);
});

const getHealthTrackerData = async () => {
  try {
    // Get active tracker
    const activeTracker = await healthTrackerService.getActiveTracker();
    state.activeTracker = activeTracker;
    
    if (activeTracker && activeTracker.tracker_template) {
      selectedTemplate.value = activeTracker.tracker_template;
      
      // Always fetch entries to show the structure
      await fetchTrackerEntries();
    }
    
    // Get upcoming entries
    const upcomingEntries = await healthTrackerService.getUpcomingEntries();
    state.upcomingEntry = upcomingEntries[0] || {};
    
    // Get latest entries
    const latestEntries = await healthTrackerService.getLatestEntries();
    state.latestEntries = latestEntries;
    
  } catch (error) {
    console.error('Error fetching health tracker data:', error);
  }
};

// Function to handle moving to the next item
const nextItem = () => {
  if (state.availableItems.length > 1) {
    state.currentItemIndex = (state.currentItemIndex + 1) % state.availableItems.length;
    filterEntriesForCurrentItem();
  }
};

// Function to handle moving to the previous item
const previousItem = () => {
  if (state.availableItems.length > 1) {
    state.currentItemIndex = state.currentItemIndex === 0 
      ? state.availableItems.length - 1 
      : state.currentItemIndex - 1;
    filterEntriesForCurrentItem();
  }
};



const fetchTrackerEntries = async () => {
  if (!currentTracker.value?.id) {
    state.allTrackerEntries = [];
    state.trackerEntriesWithValue = [];
    state.availableItems = [];
    return;
  }

  try {
    const response = await healthTrackerService.getTrackerEntries(
      currentTracker.value.id
    );
    
    // Store all entries - response is now the entries array directly
    state.allTrackerEntries = response || [];

    // Debug: log allTrackerEntries
    console.log('allTrackerEntries:', state.allTrackerEntries);

    // Extract unique items from the entries
    const itemsMap = new Map();
    
    if (response && response.length > 0) {
      response.forEach((entry: any) => {
        if (entry.tracker_template_item && !itemsMap.has(entry.item_id)) {
          itemsMap.set(entry.item_id, entry.tracker_template_item);
        }
      });
    }
    // Note: Removed the fallback to tracker_template.items since it doesn't exist in the type
    
    state.availableItems = Array.from(itemsMap.values());

    // If no current item is selected, select the first one
    if (state.availableItems.length > 0 && state.currentItemIndex >= state.availableItems.length) {
      state.currentItemIndex = 0;
    }

    // Filter entries for the current item and add response_value
    filterEntriesForCurrentItem();
    
  } catch (error) {
    console.error('Error fetching tracker entries:', error);
    state.allTrackerEntries = [];
    state.trackerEntriesWithValue = [];
    state.availableItems = [];
  }
};

const filterEntriesForCurrentItem = () => {
  if (!currentItem.value) {
    state.trackerEntriesWithValue = [];
    return;
  }

  // Filter entries for the current item and sort by due_datetime
  let filteredEntries = state.allTrackerEntries
    .filter((entry: any) => entry.item_id === currentItem.value.id)
    .sort((a: any, b: any) => new Date(a.due_datetime).getTime() - new Date(b.due_datetime).getTime())
    .map((entry: any) => ({
      ...entry,
      value: entry.response_value || 0, // Use 0 as default for null response_value
      entry_datetime: entry.due_datetime, // Use due_datetime for chart display
      isSubmitted: entry.response_value !== null, // Track if entry was submitted
    }));

  // If tracker has started but no entries exist, create a starting point from tracker start date
  if (filteredEntries.length === 0 && currentTracker.value?.start_date) {
    const startDate = moment(currentTracker.value.start_date);
    if (moment().isSameOrAfter(startDate, 'day')) {
      filteredEntries = [{
        id: 'start',
        due_datetime: currentTracker.value.start_date,
        entry_datetime: currentTracker.value.start_date,
        value: 0,
        isSubmitted: false,
        item_id: currentItem.value.id,
        tracker_template_item: currentItem.value
      }];
    }
  }

  state.trackerEntriesWithValue = filteredEntries;
};

watch(currentItem, () => {
  if (currentItem.value) {
    filterEntriesForCurrentItem();
  }
});

const getEntryDate = (entry: any) => {
  if (!entry?.entry_datetime) return { month: '', year: '' };

  return {
    month: moment(entry.entry_datetime).format('DD MMM'),
    year: moment(entry.entry_datetime).format('YYYY'),
  };
};

const getDueOrOverdue = computed(() => {
  if (!state.upcomingEntry?.due_date) return '';
  
  const today = moment();
  const entryDate = moment(state.upcomingEntry.due_date);

  if (today.isSame(entryDate, 'day')) {
    return '(Due)';
  }

  if (today.isAfter(entryDate)) {
    return '(Overdue)';
  }

  return '';
});

const disableEntryIfCompletedToday = computed(() => {
  const today = moment().format('YYYY-MM-DD');
  const currentTemplateId = state.upcomingEntry?.tracker_template_id;
  
  // Check if there's a completed entry today for the current template
  const latestEntryToday = state.latestEntries.find((entry) => 
    moment(entry.entry_datetime).format('YYYY-MM-DD') === today && 
    entry.tracker_template_id === currentTemplateId
  );

  return !!latestEntryToday;
});

const onCompleteEntry = () => {
  if (!currentTracker.value?.id || !state.upcomingEntry?.due_date) return;
  
  const trackerId = currentTracker.value.id;
  const entryDate = moment(state.upcomingEntry.due_date).format('YYYY-MM-DD');
  
  router.push({ 
    name: 'tracker-entry', 
    params: { 
      id: trackerId.toString(),
      date: entryDate 
    } 
  });
};

const upcomingButtonLabel = computed(() => {
  if (!state.upcomingEntry?.due_date) return '';
  const today = moment();
  const due = moment(state.upcomingEntry.due_date);
  if (today.isSame(due, 'day') || today.isAfter(due, 'day')) {
    return 'Completed';
  }
  return 'Coming Soon';
});

// --- Add new computed for period-based chart data ---
const trackerDuration = computed(() => {
  // Prefer the duration from the tracker template, fallback to 1
  return currentTracker.value?.tracker_template?.duration ?? 1;
});

const trackerFrequency = computed(() => {
  // Prefer the frequency from the tracker template, fallback to 'weekly'
  return currentTracker.value?.tracker_template?.frequency ?? 'weekly';
});

const getPeriodLabel = (index: number) => {
  switch (trackerFrequency.value.toLowerCase()) {
    case 'daily':
      return `D${index + 1}`;
    case 'weekly':
      return `W${index + 1}`;
    case 'monthly':
      return `M${index + 1}`;
    default:
      return `${index + 1}`;
  }
};

const periodLabels = computed(() => {
  return Array.from({ length: trackerDuration.value }, (_, i) => getPeriodLabel(i));
});

const periodBasedEntries = computed(() => {
  if (!currentItem.value) return [];
  const entries = state.allTrackerEntries
    .filter((entry: any) => entry.item_id === currentItem.value.id && entry.due_datetime)
    .sort((a: any, b: any) => new Date(a.due_datetime).getTime() - new Date(b.due_datetime).getTime());

  // Use the earliest entry's due_datetime as the start date, or fallback to tracker start_date
  let startDate: moment.Moment | null = null;
  if (entries.length > 0) {
    startDate = moment(entries[0].due_datetime);
  } else if (currentTracker.value?.start_date) {
    startDate = moment(currentTracker.value.start_date);
  }
  if (!startDate) return [];

  const periodMap = new Map<number, any>();
  entries.forEach((entry: any) => {
    const entryDate = moment(entry.due_datetime);
    let periodIdx = 0;

    switch (trackerFrequency.value.toLowerCase()) {
      case 'weekly':
        periodIdx = entryDate.startOf('isoWeek').diff(startDate.clone().startOf('isoWeek'), 'weeks');
        break;
      case 'monthly':
        periodIdx = entryDate.startOf('month').diff(startDate.clone().startOf('month'), 'months');
        break;
      case 'daily':
      default:
        periodIdx = entryDate.startOf('day').diff(startDate.clone().startOf('day'), 'days');
        break;
    }

    if (periodIdx >= 0 && periodIdx < trackerDuration.value) {
      // If there are multiple entries for the same period keep the latest submitted one
      const existing = periodMap.get(periodIdx);
      if (!existing || moment(entry.submitted_datetime ?? entry.due_datetime).isAfter(existing.submitted_datetime ?? existing.due_datetime)) {
        periodMap.set(periodIdx, entry);
      }
    }
  });

  // Build the array for the chart, filling missing periods with null
  return Array.from({ length: trackerDuration.value }, (_, i) => {
    const entry = periodMap.get(i);
    // Use response_value for charting, treat null as missing
    const chartValue = entry && entry.response_value !== null ? entry.response_value : null;
    return entry
      ? {
          ...entry,
          value: chartValue,
          isSubmitted: chartValue !== null && chartValue !== undefined,
          periodIdx: i,
        }
      : {
          value: null,
          isSubmitted: false,
          periodIdx: i,
        };
  });
});

onMounted(() => {
  getHealthTrackerData();
});

onActivated(() => {
  // Refresh data when returning from entry page
  getHealthTrackerData();
});
</script>

<style lang="scss" scoped>
:deep(.v-field--variant-solo) {
  border-radius: 10px;
  box-shadow: none;
  border: 1px solid #0000001A;
}

.chart-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  overflow-x: auto;
  overflow-y: visible;
}

.line-chart {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  height: auto;
  max-width: 100%;
}

.axis-label {
  font-size: 12px;
  fill: #666;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.value-label {
  font-size: 12px;
  fill: v-bind('settingsStore.settings.primaryColor');
  font-weight: 600;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.value-label-unsubmitted {
  font-size: 12px;
  fill: #BDBDBD;
  font-weight: 400;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.data-point {
  cursor: pointer;
  transition: r 0.2s ease;
}

.data-point:hover {
  r: 8;
}

.name {
  color: v-bind(buttonColor)
}

.overview-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.health-tracker {
  background-color: #F5F5F5;
  min-height: calc(100vh - 82px); /* Adjust for the top bar */
}

/* Additional styling classes */
.border-rounded {
  border-radius: 16px;
}

.b-round-20 {
  border-radius: 20px;
}

.p-text {
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 0;
}

.date-label {
  font-weight: 500;
  color: #666;
}

.date-color {
  color: v-bind('settingsStore.settings.primaryColor');
}

.primary-color {
  color: v-bind('settingsStore.settings.primaryColor');
}

.font-weight {
  font-weight: 600;
}

.small {
  font-size: 12px;
  font-weight: 400;
}

.complete-button {
  min-width: 100px;
  font-weight: 500;
}

.view-button {
  background-color: #f5f5f5;
  color: v-bind('settingsStore.settings.primaryColor');
  border: 1px solid v-bind('settingsStore.settings.primaryColor');
  min-width: 60px;
  font-weight: 500;
}
</style> 